import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatGel, formatPoints } from '../lib/format';
import { useI18n, useLocalized } from '../lib/i18n';
import { useReport, useStaffEarnings } from '../lib/queries';
import { Amount, Card, EmptyState, Pill, SectionTitle, Skeleton, Stat } from '../components/ui';

const TIMEZONE = 'Asia/Tbilisi';

/** Ranges are expressed in Tbilisi dates, matching how the API buckets them. */
function tbilisiToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIMEZONE,
  }).format(new Date());
}

function daysAgo(days: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIMEZONE,
  }).format(new Date(Date.now() - days * 86_400_000));
}

const PRESETS = [
  { label: 'reports.days7', days: 6 },
  { label: 'reports.days30', days: 29 },
  { label: 'reports.days90', days: 89 },
  { label: 'reports.thisYear', days: 364 },
] as const;

/**
 * Who earned what over the period.
 *
 * The figure is what survived refunds, because this is the table somebody
 * reads when deciding what to pay. What was handed back is shown beside it
 * rather than hidden, so a bad month is explainable instead of mysterious.
 */
function StaffEarnings({ from, to }: { from: string; to: string }) {
  const { t } = useI18n();
  const { data, isPending } = useStaffEarnings(from, to);

  if (isPending) return <Skeleton className="h-40" />;

  const items = data?.items ?? [];
  if (items.length === 0) return <EmptyState title={t('reports.noWork')} />;

  const peak = items[0]?.valueTetri || 1;

  return (
    <Card>
      <div className="space-y-3">
        {items.map((person) => (
          <div key={person.adminUserId}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="min-w-0 truncate text-sm text-ink">
                {person.firstName} {person.lastName}
                {person.jobTitle && (
                  <span className="ml-2 text-[0.68rem] text-ink-faint">{person.jobTitle}</span>
                )}
                {!person.isActive && <span className="ml-2"><Pill>{t('staff.inactive')}</Pill></span>}
              </span>
              <span className="shrink-0 text-right">
                <Amount
                  value={formatGel(person.valueTetri)}
                  className="block text-sm font-semibold text-ink"
                />
                <span className="text-[0.62rem] text-ink-faint">
                  {t('reports.jobsAndCars', { jobs: person.jobs, cars: person.cars })}
                  {person.refundedTetri > 0 && (
                    <span className="ml-1 text-warning">
                      {t('reports.lessRefunded', { amount: formatGel(person.refundedTetri) })}
                    </span>
                  )}
                </span>
                {/* Null for anyone not allowed to see pay — the server leaves it out. */}
                {person.payTetri !== null && (
                  <span className="tabular block text-[0.7rem] text-ink-dim">
                    {t('reports.payLine', { amount: formatGel(person.payTetri) })}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-brand/60"
                style={{ width: `${Math.max(2, (person.valueTetri / peak) * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {data && (
        <p className="mt-4 border-t border-line pt-3 text-xs text-ink-dim">
          {t('reports.earningsTotal', {
            people: data.totals.people,
            amount: formatGel(data.totals.valueTetri),
          })}
          {data.totals.payTetri !== null && (
            <span className="ml-1 font-semibold text-ink">
              {t('reports.payTotal', { amount: formatGel(data.totals.payTetri) })}
            </span>
          )}
        </p>
      )}
    </Card>
  );
}

export function ReportsPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(tbilisiToday());
  const { data, isPending } = useReport(from, to);

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('reports.title')}</h1>
      </header>

      <Card className="mb-4">
        {/* Side by side and full width on a phone; a row with the presets on a desk. */}
        <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
          <div className="flex min-w-0 flex-col gap-1.5">
            <label className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('reports.from')}</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(event) => setFrom(event.target.value)}
              className="tabular w-full min-w-0 rounded-xl border border-line bg-raised px-3 py-2.5 text-ink focus:border-brand focus:outline-none sm:w-auto sm:px-3.5"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <label className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('reports.to')}</label>
            <input
              type="date"
              value={to}
              min={from}
              max={tbilisiToday()}
              onChange={(event) => setTo(event.target.value)}
              className="tabular w-full min-w-0 rounded-xl border border-line bg-raised px-3 py-2.5 text-ink focus:border-brand focus:outline-none sm:w-auto sm:px-3.5"
            />
          </div>
          <div className="col-span-2 grid grid-cols-4 gap-1.5 sm:flex sm:flex-wrap">
            {PRESETS.map((preset) => (
              <button
                key={t(preset.label)}
                onClick={() => { setFrom(daysAgo(preset.days)); setTo(tbilisiToday()); }}
                className="inline-flex min-h-10 items-center justify-center rounded-lg border border-line bg-raised px-1 text-xs font-medium text-ink-dim transition-colors hover:border-brand/40 hover:text-ink sm:min-h-8 sm:px-2.5"
              >
                {t(preset.label)}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {isPending && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      )}

      {data && (
        <>
          <section className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label={t('reports.revenue')}
              value={<Amount value={formatGel(data.totals.revenueTetri)} />}
              sub={data.totals.discountTetri > 0
                ? t('reports.revenueAfterDiscount', { count: data.totals.orders, amount: formatGel(data.totals.discountTetri) })
                : t('dash.ordersCount', { count: data.totals.orders })}
            />
            <Stat
              label={t('reports.averageOrder')}
              value={<Amount value={formatGel(data.totals.averageOrderTetri)} />}
              sub={t('reports.customersCount', { count: data.totals.customers })}
            />
            <Stat
              label={t('reports.cashbackGiven')}
              value={<Amount value={formatGel(data.totals.cashbackEarnedTetri)} className="text-positive" />}
              sub={t('reports.cashbackGivenSub')}
            />
            <Stat
              label={t('reports.pointsIssued')}
              value={formatPoints(data.totals.pointsEarned)}
              sub={t('reports.cashbackSpent', { amount: formatGel(data.totals.cashbackUsedTetri) })}
            />
          </section>

          <section className="mb-4">
            <SectionTitle>{t('reports.staffEarnings')}</SectionTitle>
            <StaffEarnings from={from} to={to} />
          </section>

          <div className="grid gap-4 lg:grid-cols-2">
            <section>
              <SectionTitle>{t('reports.topServices')}</SectionTitle>
              {data.topServices.length === 0
                ? <EmptyState title={t('reports.noSales')} />
                : (
                  <Card>
                    <div className="space-y-2">
                      {data.topServices.map((service) => {
                        const peak = data.topServices[0]?.revenueTetri || 1;
                        return (
                          <div key={service.name.en}>
                            <div className="flex items-baseline justify-between gap-3">
                              <span className="truncate text-sm text-ink">{localized(service.name)}</span>
                              <span className="tabular shrink-0 text-sm text-ink-dim">
                                {formatGel(service.revenueTetri)}
                                <span className="ml-2 text-[0.68rem] text-ink-faint">×{service.quantity}</span>
                              </span>
                            </div>
                            <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-brand/60"
                                style={{ width: `${(service.revenueTetri / peak) * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}
            </section>

            <section>
              <SectionTitle>{t('reports.topCustomers')}</SectionTitle>
              {data.topCustomers.length === 0
                ? <EmptyState title={t('reports.noVisits')} />
                : (
                  <div className="space-y-2">
                    {data.topCustomers.map((customer) => (
                      <Link
                        key={customer.id}
                        to={`/customers/${customer.id}`}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-surface px-4 py-2.5 transition-colors hover:border-brand/40"
                      >
                        <span className="truncate text-sm text-ink">{customer.name}</span>
                        <span className="shrink-0 text-right">
                          <Amount
                            value={formatGel(customer.revenueTetri)}
                            className="block text-sm font-semibold text-ink"
                          />
                          <span className="text-[0.62rem] text-ink-faint">
                            {t('reports.ordersPlural', { count: customer.orders })}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
